/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ButtonHTMLAttributes, } from "react";
import clsx from "clsx";
import { ColorProps, } from "./types";

export type ButtonProps = {
  children?: React.ReactNode;
  fullWidth?: boolean;
  variant?: "contained" | "outlined" | "text";
  size?: "small" | "medium" | "large";
  color?: ColorProps | "default" | "error";
  disabled?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>;

export default function Button({
  children,
  fullWidth = false,
  variant = "contained",
  size = "medium",
  color = "primary",
  className,
  type = "button",
  startIcon,
  endIcon,
  style,
  ...props
}: ButtonProps): JSX.Element {
  const selectedColor = props.disabled ? "default" : color;

  const baseClasses =
    "flex items-center justify-center gap-2 rounded-md text-center font-semibold shadow-sm transition-all duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2";

  const sizeClasses = {
    small: "px-2.5 py-1.5 text-xs",
    medium: "px-3.5 py-2.5 text-sm",
    large: "px-5 py-3 text-base",
  };

  const colorClasses = {
    primary: {
      contained:
        "bg-primary text-contrast-text-primary focus-visible:outline-primary",
      outlined:
        "border border-primary text-primary focus-visible:outline-primary",
      text:
        "text-primary focus-visible:outline-primary",
    },
    secondary: {
      contained:
        "bg-secondary text-contrast-text-secondary focus-visible:outline-secondary",
      outlined:
        "border border-secondary text-secondary focus-visible:outline-secondary",
      text:
        "text-secondary focus-visible:outline-secondary",
    },
    default: {
      contained:
        "bg-gray-500 text-white focus-visible:outline-gray-800",
      outlined:
        "border border-gray-600 text-gray-600 focus-visible:outline-gray-800",
      text:
        "text-gray-600 focus-visible:outline-gray-600",
    },
    inherit: {
      contained: "bg-inherit text-inherit",
      outlined: "border border-current text-inherit",
      text: "text-inherit",
    },
    error: {
      contained:
        "bg-red-600 text-white focus-visible:outline-red-600",
      outlined:
        "border border-red-600 text-red-600 focus-visible:outline-red-600",
      text:
        "text-red-600 focus-visible:outline-red-600",
    },
  };

  return (
    <button
      style={style}
      type={type}
      disabled={props.disabled}
      className={clsx(
        baseClasses,
        sizeClasses[size],
        colorClasses[selectedColor][variant],

        // Hover mais claro para qualquer cor
        !props.disabled &&
          "hover:brightness-110 active:brightness-95",

        // Full width
        fullWidth && "w-full",

        // Variante text
        variant === "text" && "shadow-none",

        // Disabled state
        props.disabled &&
          "opacity-50 cursor-not-allowed pointer-events-none",

        "uppercase",
        className
      )}
      {...props}
    >
      {startIcon && <span className="flex shrink-0">{startIcon}</span>}
      {children}
      {endIcon && <span className="flex shrink-0">{endIcon}</span>}
    </button>
  );
}
